import { Isuccess } from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { AutoCloudDnsClient } from '../../utils/auto-cloud-dns-client';

/**
 * Interface representing a super DNS permission.
 */
export interface ISuperDnsPermission {
  id: number;
  userId: string | number;
  mainAccess: boolean;
  read: boolean;
  write: boolean;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Class representing a super DNS permission.
 */
export class DnsSuperPermission {
  /**
   * Base URL for super DNS permission API endpoints.
   */
  static baseUrl = '/super-permissions';

  /**
   * The ID of the permission.
   */
  id: number;

  /**
   * The ID of the user associated with the permission.
   */
  userId: string | number;

  /**
   * Whether the user has main access to the DNS.
   */
  mainAccess: boolean;

  /**
   * Whether the user has read access to the DNS.
   */
  read: boolean;

  /**
   * Whether the user has write access to the DNS.
   */
  write: boolean;

  /**
   * The date and time the permission was created.
   */
  createdAt: Date;

  /**
   * The date and time the permission was last updated.
   */
  updatedAt: Date;

  constructor(data: ISuperDnsPermission) {
    this.id = data.id;
    this.userId = data.userId;
    this.mainAccess = data.mainAccess;
    this.read = data.read;
    this.write = data.write;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
  }

  /**
   * Retrieves super DNS permissions for a specific user.
   *
   * @param userId - The ID of the user to retrieve permissions for.
   * @returns A Promise that resolves to a new DnsSuperPermission instance.
   */
  static async getByUser(userId: string | number) {
    const observer$ = AutoCloudDnsClient.ehttp
      .makeGet<ISuperDnsPermission>(`${DnsSuperPermission.baseUrl}/user/${userId}`);
    const data = await lastValueFrom(observer$);

    return new DnsSuperPermission(data);
  }

  /**
   * Adds or updates super DNS permissions for a specific user.
   *
   * @param permissions - The permissions to add or update.
   * @returns A Promise that resolves to an Isuccess instance.
   */
  static addUpdate(permissions: Omit<ISuperDnsPermission, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>) {
    const observer$ = AutoCloudDnsClient.ehttp
      .makePut<Isuccess>(`${DnsSuperPermission.baseUrl}/${permissions.userId}`, permissions);

    return lastValueFrom(observer$);
  }

  /**
   * Removes super DNS permissions for a specific user.
   *
   * @returns A Promise that resolves to an Isuccess instance.
   */
  remove() {
    return AutoCloudDnsClient.ehttp
      .makeDelete<Isuccess>(`${DnsSuperPermission.baseUrl}/${this.userId}`);
  }
}
